import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdArrowBack, MdArrowForward, MdSave, MdImage } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import Card from "components/card";
import FileUploader, { type UploadedFile } from "components/ui/FileUploader";
import {
  instructorCoursesService,
  buildPricingPayload,
  type CourseUpsertBody,
  type CoursePricingType,
} from "services/instructorCoursesService";
import { instructorAuthService, type InstructorAuthUser } from "services/instructorAuthService";
import BasicAcademicAssignmentSelects from "../components/BasicAcademicAssignmentSelects";
import CoursePricingFields from "../components/CoursePricingFields";
import { toastError, toastSuccess } from "lib/toast";

export default function NewCoursePage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();

  const [form, setForm] = React.useState<CourseUpsertBody>({
    title: "",
    description: null,
    image: null,
    subject_id: 0,
  });
  const [profile, setProfile] = React.useState<InstructorAuthUser | null>(null);
  const [stageId, setStageId] = React.useState<number | null>(null);
  const [gradeId, setGradeId] = React.useState<number | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(null);
  const [pricingType, setPricingType] = React.useState<CoursePricingType>("free");
  const [price, setPrice] = React.useState("");
  const [priceError, setPriceError] = React.useState<string | null>(null);

  const assignedStages = profile?.stages ?? [];
  const assignedGrades = profile?.grades ?? [];
  const assignedSubjects = profile?.subjects ?? [];

  React.useEffect(() => {
    (async () => {
      try {
        const instructorProfile = await instructorAuthService.getProfile();
        setProfile(instructorProfile ?? null);
      } catch {
        // silent
      }
    })();
  }, []);

  const handleImageChange = (files: UploadedFile[]) => {
    const done = files.find((f) => f.status === "done" && f.key);
    if (done?.key) {
      setForm((p) => ({ ...p, image: done.key }));
      setImagePreviewUrl(done.localPreviewUrl || done.url || null);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toastError(isArabic ? "العنوان مطلوب" : "Title is required");
      return;
    }
    if (!stageId) {
      toastError(isArabic ? "المرحلة مطلوبة" : "Stage is required");
      return;
    }
    if (!gradeId) {
      toastError(isArabic ? "الصف مطلوب" : "Grade is required");
      return;
    }
    if (!form.subject_id) {
      toastError(isArabic ? "المادة مطلوبة" : "Material is required");
      return;
    }
    if (pricingType === "paid" && !(Number(price) > 0)) {
      setPriceError(isArabic ? "أدخل سعرًا أكبر من صفر" : "Enter a price greater than 0");
      toastError(isArabic ? "سعر الكورس مطلوب" : "Course price is required");
      return;
    }
    setPriceError(null);
    setSaving(true);
    try {
      const created = await instructorCoursesService.create({
        title: form.title.trim(),
        description: form.description?.trim() || null,
        image: form.image || null,
        subject_id: Number(form.subject_id),
        grade_id: gradeId,
        ...buildPricingPayload({ pricingType, price }),
      });
      const createdId =
        (created as any)?.id ??
        (created as any)?.course?.id ??
        (created as any)?.result?.id ??
        (created as any)?.result?.course?.id ??
        null;
      if (createdId != null) {
        navigate(`/instructor/courses/${createdId}/overview`);
      } else {
        toastSuccess(isArabic ? "تم إنشاء الكورس بنجاح" : "Course created successfully");
        navigate("/instructor/courses");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-5 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <Link
          to="/instructor/courses"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-500 hover:underline"
        >
          {isArabic ? <MdArrowForward className="h-4 w-4" /> : <MdArrowBack className="h-4 w-4" />}
          {isArabic ? "العودة للكورسات" : "Back to courses"}
        </Link>
      </div>

      <Card extra="w-full p-4 md:p-6">
        <header className="mb-4">
          <h1 className="text-xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "كورس جديد" : "New course"}
          </h1>
          <p className="text-sm text-gray-500">
            {isArabic
              ? "املأ البيانات الأساسية ثم يمكنك إضافة المحتوى والاختبارات لاحقًا."
              : "Fill the basics; you can add content and quizzes after creation."}
          </p>
        </header>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-2">
            {isArabic ? "العنوان *" : "Title *"}
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder={isArabic ? "مثلاً: تشريح 101" : "e.g. Anatomy 101"}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-2">
            {isArabic ? "الوصف" : "Description"}
            <textarea
              value={form.description ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={4}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <div className="md:col-span-2">
            <h2 className="text-sm font-bold text-navy-700 dark:text-white">
              {isArabic ? "التعيين الأكاديمي" : "Academic assignment"}
            </h2>
            <p className="text-xs text-gray-500">
              {isArabic
                ? "اختر المسار الأكاديمي ثم المادة من تعييناتك."
                : "Choose the academic path, then the material from your assignments."}
            </p>
          </div>
          <BasicAcademicAssignmentSelects
            isArabic={isArabic}
            stages={assignedStages}
            grades={assignedGrades}
            subjects={assignedSubjects}
            stageId={stageId}
            gradeId={gradeId}
            subjectId={form.subject_id || null}
            onStageChange={(id) => {
              setStageId(id);
              setGradeId(null);
              setForm((p) => ({ ...p, subject_id: 0 }));
            }}
            onGradeChange={(id) => {
              setGradeId(id);
              setForm((p) => ({ ...p, subject_id: 0 }));
            }}
            onSubjectChange={(id) => setForm((p) => ({ ...p, subject_id: id ?? 0 }))}
          />
          <CoursePricingFields
            isArabic={isArabic}
            pricingType={pricingType}
            price={price}
            onPricingTypeChange={(next) => {
              setPricingType(next);
              if (next === "free") {
                setPrice("");
                setPriceError(null);
              }
            }}
            onPriceChange={(next) => {
              setPrice(next);
              if (priceError) setPriceError(null);
            }}
            priceError={priceError}
          />
          <div className="md:col-span-2">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "صورة الكورس" : "Course image"}
            </p>
            <div className="mt-1 flex flex-col gap-3 md:flex-row">
              <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-white/5 md:w-44">
                {imagePreviewUrl ? (
                  <img
                    src={imagePreviewUrl}
                    alt={isArabic ? "صورة الكورس" : "Course image"}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                      setImagePreviewUrl(null);
                    }}
                  />
                ) : (
                  <MdImage className="h-8 w-8" />
                )}
              </div>
              <FileUploader
                group="documents"
                accept="image/*"
                multiple={false}
                onChange={handleImageChange}
                className="flex-1"
                hint={isArabic ? "مسموح بصور فقط" : "Images only"}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-white/10">
          <Link
            to="/instructor/courses"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            <MdSave className="h-5 w-5" />
            {saving ? (isArabic ? "جارٍ الإنشاء..." : "Creating...") : isArabic ? "إنشاء الكورس" : "Create course"}
          </button>
        </div>
      </Card>
    </div>
  );
}
